import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import dayjs from 'dayjs'
import * as _ from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { FileUploader } from '@/components/forms/FileUploader'
import { AddIcon, DocumentCheckIcon, MinusIcon, PencilIcon, TrashIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { Tooltip } from '@/components/ui/Tooltip'
import {
  createLineItemFile,
  deleteLineItem,
  deleteLineItemFile,
  updateLineItem,
} from '@/services/lineItem.service'
import {
  createLineItemActionNeeded,
  deleteLineItemActionNeeded,
  updateLineItemActionNeeded,
} from '@/services/lineItems.service'
import { UserStoreContext } from '@/stores/UserStore'
import { stripHtmlTags, toast } from '@/utils/helpers'

import RepairOrderActionItem from './RepairOrderActionItem'
import ResolveLineItemModal from './ResolveLineItemModal'
import WriteOffLineItemModal from './WriteOffLineItemModal'

const EMPTY_ACTION_ITEM = {
  action: { id: '', name: '' },
  completedAt: null,
  id: 'temp-0',
  markedCompleteBy: null,
  problem: '<p><br></p>',
}

/**
 *
 * RepairOrderLineItem
 *
 */
const RepairOrderLineItem = ({
  actionOptions,
  dealerId,
  defaultOpen,
  isDealerReview,
  isRepairOrderResolved,
  getUpdatedRepairOrder,
  lineItem,
  onTransferrableChange,
  openEditModal,
  repairOrderId,
  roIsOpen,
}) => {
  // Context
  const {
    isDealerAdmin,
    isDealerService,
    isDealerUser,
    isReadOnlySession,
    isWpcAdmin,
    isWpcUser,
    canEditProblemWithoutTimestamps,
  } = useContext(UserStoreContext)

  // If the user is a WPC user or if they are a Dealer Admin, they are on the admin side of the
  // repair order and have the same permissions. This is to encorporate Dealer Managed workflows.
  const isROAdminUser = isWpcUser || isDealerAdmin
  const isROServiceUser = isDealerUser && !isDealerAdmin

  // State
  const [loading, setLoading] = useState(false)
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false)
  const [selectedFileToDelete, setSelectedFileToDelete] = useState({})
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false)
  const [updatedLineItem, setUpdatedLineItem] = useState({
    ...lineItem,
    actionsNeeded:
      lineItem.actionsNeeded && lineItem.actionsNeeded.length > 0
        ? lineItem.actionsNeeded
        : [{ ...EMPTY_ACTION_ITEM, id: `temp-${Date.now()}-${lineItem.id}` }],
  })
  const [isWriteOffModalOpen, setIsWriteOffModalOpen] = useState(false)

  // Sync local state when the lineItem prop changes (e.g. after a silent refetch)
  useEffect(() => {
    setUpdatedLineItem((prev) => ({
      ...lineItem,
      // Preserve local actionsNeeded if they contain unsaved temp items,
      // otherwise use the fresh data from the prop
      actionsNeeded:
        lineItem.actionsNeeded && lineItem.actionsNeeded.length > 0
          ? lineItem.actionsNeeded
          : prev.actionsNeeded,
    }))
  }, [lineItem])

  const handleSuccess = (message) => toast(message, 'success')
  const handleError = (message) => toast(message, 'error')

  const debounceHandleSuccess = useCallback(
    _.debounce(handleSuccess, 1000, { leading: false, trailing: true }),
    [],
  )

  const isResolvable = useMemo(() => {
    const hasTechnician =
      updatedLineItem.technician && updatedLineItem.technician.trim().length > 0
    const hasActions = updatedLineItem.actionsNeeded && updatedLineItem.actionsNeeded.length > 0
    const allActionsComplete = updatedLineItem.actionsNeeded.every(
      (action) => action.completedAt != null,
    )
    const allActionsHaveProblems = updatedLineItem.actionsNeeded.every(
      (action) => action.problem && stripHtmlTags(action.problem).length > 0,
    )

    return allActionsHaveProblems && hasActions && allActionsComplete && hasTechnician
  }, [updatedLineItem])

  const isRepairOrderTransferrable = useMemo(() => {
    const hasTechnician =
      updatedLineItem.technician && updatedLineItem.technician.trim().length > 0
    const hasActions = updatedLineItem.actionsNeeded && updatedLineItem.actionsNeeded.length > 0
    const allActionsHaveProblems = updatedLineItem.actionsNeeded.every(
      (action) => action.problem && stripHtmlTags(action.problem).length > 0,
    )

    return allActionsHaveProblems && hasActions && hasTechnician
  }, [updatedLineItem])

  useEffect(() => {
    onTransferrableChange(isRepairOrderTransferrable)
  }, [isRepairOrderTransferrable])

  const updateActionNeeded = async (actionNeededId, newActionId, callback) => {
    const successHandler = (message, newActionNeededId = actionNeededId) => {
      handleSuccess(message)
      callback(newActionNeededId)
    }

    if (actionNeededId.includes('temp')) {
      createLineItemActionNeeded(
        dealerId,
        repairOrderId,
        updatedLineItem.id,
        {
          action: newActionId,
        },
        setLoading,
        handleError,
        successHandler,
      )
    } else {
      updateLineItemActionNeeded(
        dealerId,
        repairOrderId,
        updatedLineItem.id,
        actionNeededId,
        {
          action: newActionId,
        },
        setLoading,
        handleError,
        successHandler,
      )
    }
  }

  const updateActionItemStatus = (actionNeededId, checkedValue) => {
    const actionIndex = _.findIndex(updatedLineItem.actionsNeeded, (a) => a.id === actionNeededId)

    if (actionIndex !== -1) {
      const completedAt = checkedValue ? dayjs().format() : null

      updateLineItemActionNeeded(
        dealerId,
        repairOrderId,
        updatedLineItem.id,
        actionNeededId,
        {
          completedAt,
        },
        setLoading,
        handleError,
        (message) => {
          handleSuccess(message)
          const updatedItems = [...updatedLineItem.actionsNeeded]
          updatedItems[actionIndex] = { ...updatedItems[actionIndex], completedAt }
          setUpdatedLineItem((prevState) => ({ ...prevState, actionsNeeded: updatedItems }))
        },
      )
    }
  }

  const saveActionNeeded = (id, newActionId, name) => {
    const itemIndex = _.findIndex(updatedLineItem.actionsNeeded, (item) => item.id === id)
    if (itemIndex !== -1) {
      const callback = (actionId) => {
        const updatedItems = [...updatedLineItem.actionsNeeded]
        updatedItems[itemIndex] = {
          ...updatedItems[itemIndex],
          id: actionId,
          action: { ...updatedItems[itemIndex].action, id: newActionId, name },
        }
        setUpdatedLineItem((prevState) => ({ ...prevState, actionsNeeded: updatedItems }))
      }

      updateActionNeeded(updatedLineItem.actionsNeeded[itemIndex].id, newActionId, callback)
    }
  }

  // Debounced API save for action problem updates
  const debouncedSaveActionProblem = useCallback(
    _.debounce((actionNeededId, value) => {
      updateLineItemActionNeeded(
        dealerId,
        repairOrderId,
        lineItem.id,
        actionNeededId,
        { problem: value },
        () => {},
        handleError,
        () => {
          debounceHandleSuccess('Problem updated.')
        },
      )
    }, 1000),
    [dealerId, repairOrderId, lineItem.id],
  )

  // Cancel pending debounced save on unmount
  useEffect(() => () => debouncedSaveActionProblem.cancel(), [debouncedSaveActionProblem])

  /**
   * Handles saving a problem value for a specific action needed.
   * Updates local state immediately and debounces the API call.
   * @param {string} actionNeededId
   * @param {string} value
   */
  const updateActionProblem = useCallback(
    (actionNeededId, value) => {
      // Update local state immediately using functional form to avoid stale closures
      setUpdatedLineItem((prevState) => {
        const actionIndex = _.findIndex(prevState.actionsNeeded, (a) => a.id === actionNeededId)
        if (actionIndex === -1) return prevState

        const updatedItems = [...prevState.actionsNeeded]
        updatedItems[actionIndex] = { ...updatedItems[actionIndex], problem: value }
        return { ...prevState, actionsNeeded: updatedItems }
      })

      // For temp (unsaved) actions, only update local state
      if (String(actionNeededId).includes('temp')) return

      debouncedSaveActionProblem(actionNeededId, value)
    },
    [debouncedSaveActionProblem],
  )

  /**
   * Handles downloading the specified `file`.
   * @param {object} e
   * @param {object} file
   */
  const downloadFile = async (e, file) => {
    e.preventDefault()

    try {
      const url = file.file
      const response = await fetch(url)
      const blobUrl = await response.blob()
      const objectUrl = window.URL.createObjectURL(blobUrl)
      const downloadLink = document.createElement('a')
      downloadLink.href = objectUrl
      downloadLink.download = file.displayName
      downloadLink.click()
      downloadLink.remove()
    } catch (err) {
      handleError('Error downloading file.')
    }
  }

  /**
   * Determines whether or not we display the Write Off button,
   * and if so, whether or not it should be disabled.
   */
  const renderWriteOffButton = () => {
    // Do not display write off button when:
    // - Dealer user and the RO is in WPC review
    // - Dealer user and the user has a role of `Service`
    if (isROServiceUser && !isDealerReview) return null
    if (isROServiceUser && isDealerService) return null

    // Display, but disable the write off button when:
    // The line item is not transferrable or the state is not pending
    // The session cannot write: the WPC `Read Only` role, or an active impersonation
    // WPC user with the role of `Admin` and the line item is more than $1000
    let disabled = false
    if (!isRepairOrderTransferrable || updatedLineItem.state !== 'Pending') disabled = true
    if (isReadOnlySession) disabled = true
    if (isWpcAdmin && updatedLineItem.claimAmount >= 1000) {
      disabled = true
    }

    return (
      <Tooltip
        display={!isRepairOrderTransferrable || updatedLineItem.state !== 'Pending'}
        content={
          <div className="bg-charcoal-950 max-w-[350px] rounded-md p-2">
            <p className="text-sm text-white">
              Each action must have a problem description and Tech # cannot be empty to write off.
            </p>
          </div>
        }
        placement="top"
      >
        <Button
          background="bg-green"
          className="w-full sm:w-auto"
          dataTestId={`writeOffLineItem-${updatedLineItem.id}`}
          label="Write Off"
          onClick={() => setIsWriteOffModalOpen(true)}
          size="sm"
          disabled={disabled}
        />
      </Tooltip>
    )
  }

  return (
    <Disclosure as="div" className="mb-8 mt-2" defaultOpen={defaultOpen}>
      {({ open }) => (
        <div>
          <span className="flex w-full items-center justify-between">
            <span className="flex">
              <DisclosureButton
                className="rounded-lg pr-4 text-xl font-bold leading-none"
                data-testid={`expandLineItem-${updatedLineItem.id}`}
              >
                {open ? (
                  <MinusIcon className="h-4 w-4 stroke-gray-700" />
                ) : (
                  <AddIcon className="h-4 w-4 stroke-gray-700" />
                )}
              </DisclosureButton>
              <div>
                <div className="flex items-center">
                  <span className="text-xl font-bold leading-none text-gray-700">
                    Line ID:
                    <span className="ml-1 uppercase">{updatedLineItem.lineName}</span>
                  </span>
                  {isROAdminUser && !isRepairOrderResolved && (
                    <button
                      aria-label={`Edit Line Item: ${updatedLineItem.lineName}`}
                      data-testid={`editLineItem-${updatedLineItem.id}`}
                      onClick={() => {
                        openEditModal(updatedLineItem)
                      }}
                      className="inline-flex items-center rounded-lg p-2 text-center hover:bg-gray-100 disabled:cursor-not-allowed"
                      type="button"
                      disabled={isReadOnlySession}
                    >
                      <PencilIcon className="h-4 stroke-white" />
                    </button>
                  )}
                </div>
                <span className="text-charcoal text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                  Tech #:
                </span>
                <span
                  className="ml-1 text-sm text-gray-700"
                  data-testid={`technicianText-${updatedLineItem.id}`}
                >
                  {updatedLineItem.technician || '—'}
                </span>
              </div>
            </span>
            <span>
              {updatedLineItem.state !== 'Pending' && (
                <span className="bg-brownGray-200 text-charcoal-900 mr-2 rounded-lg px-4 py-1 text-sm font-semibold uppercase">
                  {updatedLineItem.state}
                </span>
              )}
              <span
                className={`text-xl font-bold leading-none text-gray-700 ${
                  updatedLineItem.state === 'Pending' ? '' : 'line-through'
                }`}
              >
                {Number(updatedLineItem.claimAmount).toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })}
              </span>
            </span>
          </span>

          <DisclosurePanel className="bg-brownGray-100 mt-7 rounded-2xl px-7 pb-2 pt-4 text-gray-500 ">
            <p className="pl-4 text-xl font-bold leading-none text-gray-700">Details</p>

            {/* Actions Needed - full width, two-column layout */}
            <div className="px-4 pt-6">
              {/* Column headers — mirrors the grid in RepairOrderActionItem */}
              <div className="hidden gap-2 pb-1 md:grid md:grid-cols-2">
                <p className="text-charcoal text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                  Action Needed
                </p>
                <div className="flex flex-row gap-2">
                  <p className="text-charcoal flex-1 text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                    Problem
                  </p>
                  <p className="text-charcoal w-20 shrink-0 text-center text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                    Completed
                  </p>
                </div>
              </div>

              {updatedLineItem.actionsNeeded.map((item) => (
                <RepairOrderActionItem
                  actionOptions={actionOptions}
                  canEdit={isROAdminUser && !isReadOnlySession}
                  canEditProblemWithoutTimestamps={canEditProblemWithoutTimestamps}
                  item={item}
                  isLineItemPending={updatedLineItem.state === 'Pending'}
                  isROServiceUser={isROServiceUser}
                  key={item.id}
                  loading={loading}
                  onProblemChange={updateActionProblem}
                  onDelete={(id) => {
                    const updatedActionsNeeded = updatedLineItem.actionsNeeded.filter(
                      (i) => id !== i.id,
                    )

                    if (!id.includes('temp')) {
                      deleteLineItemActionNeeded(
                        dealerId,
                        repairOrderId,
                        lineItem.id,
                        id,
                        setLoading,
                        handleError,
                        (message) => {
                          handleSuccess(message)
                          setUpdatedLineItem((prevState) => ({
                            ...prevState,
                            actionsNeeded: updatedActionsNeeded,
                          }))
                        },
                      )
                    } else {
                      handleSuccess('Action needed deleted.')
                      setUpdatedLineItem((prevState) => ({
                        ...prevState,
                        actionsNeeded: updatedActionsNeeded,
                      }))
                    }
                  }}
                  roIsOpen={roIsOpen}
                  saveActionNeeded={saveActionNeeded}
                  updateActionItemStatus={updateActionItemStatus}
                />
              ))}
              {updatedLineItem.actionsNeeded.length === 0 ? (
                <p className="my-3 text-center">No actions needed.</p>
              ) : null}
              {isROAdminUser && (
                <div className="flex justify-end pt-2">
                  <Button
                    background="bg-background"
                    dataTestId={`addAction-${lineItem.id}`}
                    onClick={() => {
                      const newItem = {
                        ...EMPTY_ACTION_ITEM,
                        id: `temp-${Date.now()}`,
                      }

                      setUpdatedLineItem((prevState) => ({
                        ...prevState,
                        actionsNeeded: [...prevState.actionsNeeded, newItem],
                      }))
                    }}
                    outlined
                    label="Add Action"
                    size="sm"
                    disabled={updatedLineItem.state !== 'Pending' || isReadOnlySession}
                  />
                </div>
              )}
            </div>

            <div aria-hidden="true" className="border-brownGray-200 m-4 border-b-2"></div>

            <div className="grid grid-cols-1 gap-14 px-4 md:grid-cols-2">
              <div>
                <p className="text-charcoal mb-4 pb-1 text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                  Documentation
                </p>

                <div>
                  {updatedLineItem.files.map((file) => (
                    <div key={file.id} className="mb-2 flex justify-between text-blue-800">
                      <div className="flex items-center overflow-x-hidden">
                        <DocumentCheckIcon className="mr-2 h-5 w-5" />
                        <a
                          className="underline"
                          href={file.file}
                          onClick={(event) => downloadFile(event, file)}
                          target="_blank"
                        >
                          {file.displayName}
                        </a>
                      </div>
                      <button
                        aria-label="Delete"
                        type="button"
                        onClick={() => {
                          setSelectedFileToDelete(file)
                          setIsDeleteFileModalOpen(true)
                        }}
                        className="inline-flex items-center rounded-lg bg-transparent text-center text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-gray-300 disabled:cursor-not-allowed"
                        disabled={updatedLineItem.state !== 'Pending' || isReadOnlySession}
                      >
                        <TrashIcon className="text-charcoal-800 h-5 w-5" />
                      </button>
                    </div>
                  ))}

                  {updatedLineItem.files.length === 0 ? (
                    <p className="my-3 text-center">No files uploaded.</p>
                  ) : null}

                  <Modal
                    open={isDeleteFileModalOpen}
                    title="Are you sure?"
                    setOpen={setIsDeleteFileModalOpen}
                    content={
                      <div className="my-8 flex h-full items-center justify-center">
                        <p className="text-center">
                          {`Please confirm that you'd like to delete this file?`}
                        </p>
                      </div>
                    }
                    actions={[
                      {
                        type: 'submit',
                        label: 'Delete',
                        onClick: async () => {
                          const fileId = selectedFileToDelete.id
                          const updatedFiles = updatedLineItem.files.filter(
                            (file) => file.id !== fileId,
                          )
                          await deleteLineItemFile(
                            dealerId,
                            repairOrderId,
                            lineItem.id,
                            fileId,
                            setLoading,
                            handleError,
                            (message) => {
                              handleSuccess(message)
                              setUpdatedLineItem((prevState) => ({
                                ...prevState,
                                files: updatedFiles,
                              }))
                              setIsDeleteFileModalOpen(false)
                            },
                          )
                        },
                      },
                      { type: 'cancel', label: 'Cancel' },
                    ]}
                  />
                </div>
              </div>

              <div className="bg-white-light rounded-md border-[1px]">
                <div className="p-4">
                  <div
                    className={`border-[1px] border-dotted border-blue-500 px-2 ${
                      updatedLineItem.state !== 'Pending' || isReadOnlySession
                        ? 'hover:cursor-not-allowed'
                        : ''
                    }`}
                  >
                    <FileUploader
                      label="Upload Files"
                      autoSave
                      acceptedFileTypes={[
                        'image/*',
                        'application/pdf',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      ]}
                      maxFiles={1}
                      id="file"
                      type="manual"
                      className="bg-white-light min-h-130 mb-0"
                      handleUploadToServer={async (file) => {
                        const formData = new FormData()
                        formData.append('file', file)
                        await createLineItemFile(
                          dealerId,
                          repairOrderId,
                          lineItem.id,
                          formData,
                          setLoading,
                          handleError,
                          (message, newLineItemFile) => {
                            handleSuccess(message)
                            setUpdatedLineItem((prevState) => ({
                              ...prevState,
                              files: [...updatedLineItem.files, newLineItemFile],
                            }))
                          },
                        )
                      }}
                      disabled={updatedLineItem.state !== 'Pending' || isReadOnlySession}
                    />
                  </div>
                </div>
              </div>
            </div>

            {updatedLineItem.state === 'Resolved - Written Off' && (
              <>
                <div aria-hidden="true" className="border-brownGray-200 m-4 border-b-2"></div>
                <div className="mt-4 grid grid-cols-1 gap-14 px-4 md:grid-cols-2">
                  <div>
                    <p className="text-charcoal mb-2 pb-1 text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                      Write Off Reason
                    </p>
                    <p className="text-charcoal-900 text-sm font-semibold  leading-[14px]">
                      {`${updatedLineItem.writeOffDepartment} - ${
                        updatedLineItem.writeOffNotes || 'No write off reason provided.'
                      }`}
                    </p>
                  </div>

                  <div>
                    <p className="text-charcoal mb-2 pb-1 text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
                      Write Off Authorized By
                    </p>
                    <p className="text-charcoal-900 text-sm font-semibold  leading-[14px]">
                      {updatedLineItem.writtenOffBy?.displayName}
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="flex flex-col justify-end space-y-2 p-4 sm:flex-row sm:space-x-4 sm:space-y-0">
              {updatedLineItem.state === 'Pending' && (
                <>
                  {isROAdminUser && !isDealerReview && (
                    <Tooltip
                      display={!isResolvable && updatedLineItem.state === 'Pending'}
                      content={
                        <div className="bg-charcoal-950 max-w-[350px] rounded-md p-2">
                          <p className="text-sm text-white">
                            Each action must have a problem description and Tech # cannot be empty.
                          </p>
                        </div>
                      }
                      placement="top"
                    >
                      <Button
                        background="bg-background"
                        className="w-full sm:w-auto"
                        label="Resolve"
                        dataTestId={`resolveLineItem-${lineItem.id}`}
                        onClick={() => setIsResolveModalOpen(true)}
                        size="sm"
                        disabled={
                          !isResolvable ||
                          (isResolvable && updatedLineItem.state !== 'Pending') ||
                          isReadOnlySession
                        }
                      />
                    </Tooltip>
                  )}

                  {renderWriteOffButton()}

                  {isROAdminUser && !isDealerReview && (
                    <Button
                      background="bg-red"
                      dataTestId={`deleteLineItem-${lineItem.id}`}
                      className="w-full sm:w-auto"
                      label="Delete Line Item"
                      onClick={async () => {
                        await deleteLineItem(
                          dealerId,
                          repairOrderId,
                          lineItem.id,
                          setLoading,
                          handleError,
                          handleSuccess,
                        )
                        await getUpdatedRepairOrder()
                      }}
                      size="sm"
                      disabled={updatedLineItem.state !== 'Pending' || isReadOnlySession}
                      showConfirmDialog
                      confirm={{
                        title: `Delete Line Item ${updatedLineItem.lineName}`,
                        type: 'warning',
                        message: `Are you sure you want to delete this line item (Line ID: ${updatedLineItem.lineName})?`,
                      }}
                    />
                  )}
                </>
              )}
              {isROAdminUser && updatedLineItem.state !== 'Pending' && (
                <Button
                  background="bg-background"
                  className="w-full sm:w-auto"
                  dataTestId={`unresolveLineItem-${lineItem.id}`}
                  disabled={isReadOnlySession}
                  label="Unresolve"
                  onClick={async () => {
                    await updateLineItem(
                      dealerId,
                      repairOrderId,
                      updatedLineItem.id,
                      { markAsUnresolved: true },
                      setLoading,
                      handleError,
                      handleSuccess,
                    )
                    await getUpdatedRepairOrder()
                  }}
                  size="sm"
                  showConfirmDialog
                  confirm={{
                    title: `Unresolve Line Item ${updatedLineItem.lineName}`,
                    type: 'warning',
                    message: `Are you sure you want to unresolve this line item (Line ID: ${updatedLineItem.lineName})?`,
                  }}
                />
              )}

              <ResolveLineItemModal
                isOpen={isResolveModalOpen}
                setIsOpen={setIsResolveModalOpen}
                dealerId={dealerId}
                repairOrderId={repairOrderId}
                setUpdatedLineItem={setUpdatedLineItem}
                lineItem={updatedLineItem}
                getUpdatedRepairOrder={getUpdatedRepairOrder}
              />

              <WriteOffLineItemModal
                isOpen={isWriteOffModalOpen}
                setIsOpen={setIsWriteOffModalOpen}
                dealerId={dealerId}
                repairOrderId={repairOrderId}
                setUpdatedLineItem={setUpdatedLineItem}
                lineItem={updatedLineItem}
                getUpdatedRepairOrder={getUpdatedRepairOrder}
              />
            </div>
          </DisclosurePanel>
        </div>
      )}
    </Disclosure>
  )
}

export default RepairOrderLineItem

RepairOrderLineItem.propTypes = {
  actionOptions: PropTypes.array.isRequired,
  dealerId: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool,
  getUpdatedRepairOrder: PropTypes.func.isRequired,
  isDealerReview: PropTypes.bool.isRequired,
  isRepairOrderResolved: PropTypes.bool.isRequired,
  lineItem: PropTypes.object.isRequired,
  onTransferrableChange: PropTypes.func.isRequired,
  openEditModal: PropTypes.func.isRequired,
  repairOrderId: PropTypes.string.isRequired,
  roIsOpen: PropTypes.bool.isRequired,
}
